/**
 * The permission catalogue. Permissions are `resource:action` strings; team roles
 * are sets of these, fully editable per team. Role templates seed new teams.
 */
export const PERMISSION_GROUPS = [
  { resource: "properties", label: "Properties", actions: ["read", "write", "delete"] },
  { resource: "owners", label: "Tax owners & ownership", actions: ["read", "write", "delete", "reveal"] },
  { resource: "tenancies", label: "Tenants, tenancies & rent", actions: ["read", "write", "delete"] },
  { resource: "income", label: "Income", actions: ["read", "write", "delete", "export"] },
  { resource: "expenses", label: "Expenses", actions: ["read", "write", "delete", "export"] },
  { resource: "finance", label: "Mortgages & finance", actions: ["read", "write", "delete"] },
  { resource: "contractors", label: "Contractors & suppliers", actions: ["read", "write", "delete"] },
  { resource: "tasks", label: "Tasks", actions: ["read", "write", "delete"] },
  { resource: "maintenance", label: "Maintenance", actions: ["read", "write", "delete"] },
  { resource: "communications", label: "Communications", actions: ["read", "write", "delete"] },
  { resource: "compliance", label: "Compliance", actions: ["read", "write", "delete"] },
  { resource: "documents", label: "Documents", actions: ["read", "write", "delete"] },
  { resource: "reports", label: "Reports", actions: ["read", "export"] },
  { resource: "tax", label: "Tax & MTD", actions: ["read", "export", "submit"] },
  { resource: "audit", label: "Audit log", actions: ["read"] },
  { resource: "team", label: "Team settings", actions: ["manage"] },
  { resource: "members", label: "Members & invitations", actions: ["manage"] },
  { resource: "roles", label: "Roles & permissions", actions: ["manage"] },
] as const;

type Group = (typeof PERMISSION_GROUPS)[number];
export type Permission = {
  [G in Group as G["resource"]]: `${G["resource"]}:${G["actions"][number]}`;
}[Group["resource"]];

export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.actions.map((a) => `${g.resource}:${a}` as Permission),
);

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as string[]).includes(value);
}

const OPERATIONAL = [
  "properties",
  "owners",
  "tenancies",
  "income",
  "expenses",
  "finance",
  "contractors",
  "tasks",
  "maintenance",
  "communications",
  "compliance",
  "documents",
] as const;

function perms(filter: (resource: string, action: string) => boolean): Permission[] {
  return ALL_PERMISSIONS.filter((p) => {
    const [r, a] = p.split(":") as [string, string];
    return filter(r, a);
  });
}

export const ROLE_TEMPLATES: ReadonlyArray<{
  key: string;
  name: string;
  description: string;
  permissions: Permission[];
}> = [
  {
    key: "owner",
    name: "Owner",
    description: "Full access, including team, members and roles.",
    permissions: [...ALL_PERMISSIONS],
  },
  {
    key: "admin",
    name: "Admin",
    description: "Full access to records and members; cannot edit roles.",
    permissions: perms((r) => r !== "roles"),
  },
  {
    key: "manager",
    name: "Manager",
    description: "Day-to-day management of properties, tenancies, finances and operations.",
    permissions: perms(
      (r, a) =>
        ((OPERATIONAL as readonly string[]).includes(r) && a !== "export") ||
        (r === "reports" && a === "read") ||
        (r === "tax" && a === "read"),
    ),
  },
  {
    key: "accountant",
    name: "Accountant",
    description: "Read-only access to records, plus reports, tax and exports.",
    permissions: perms(
      (r, a) =>
        a === "read" ||
        a === "export" ||
        (r === "tax" && a === "submit") ||
        (r === "owners" && a === "reveal") ||
        (r === "audit" && a === "read"),
    ),
  },
  {
    key: "viewer",
    name: "Viewer",
    description: "Read-only access to records and reports.",
    permissions: perms((r, a) => a === "read" && r !== "audit"),
  },
];

export function hasPermission(granted: Iterable<string>, required: Permission): boolean {
  for (const p of granted) if (p === required) return true;
  return false;
}
